import React, { useState, useEffect } from 'react';
import {
  Plus, Search, Edit2, Trash2, Package, Monitor, Smartphone,
  Car, Printer, HardDrive, ChevronDown, X, Save, MapPin,
  Calendar, IndianRupee, User, Tag, Hash, FileText, Building2
} from 'lucide-react';
import { assetAPI, establishmentAPI, employeeAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth, useActiveEstId } from './AuthContext';
import DateInput from './DateInput';
import './UpdateEmployee.css';
import './AttendanceRegistry.css';

const Asset = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [assets, setAssets] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [stats, setStats] = useState({});

  const [formData, setFormData] = useState({
    establishment_id: lockedEstId || '',
    asset_tag: '',
    asset_type: '',
    brand_model: '',
    serial_no: '',
    assigned_to_name: '',
    assigned_to_id: '',
    status: 'Available',
    location: '',
    purchase_date: '',
    purchase_cost: '',
    warranty_expiry: '',
    notes: ''
  });

  const assetTypes = [
    { value: 'Laptop', label: 'Laptop', icon: Monitor },
    { value: 'Desktop', label: 'Desktop', icon: Monitor },
    { value: 'Mobile', label: 'Mobile Phone', icon: Smartphone },
    { value: 'Tablet', label: 'Tablet', icon: Smartphone },
    { value: 'Printer', label: 'Printer', icon: Printer },
    { value: 'Vehicle', label: 'Vehicle', icon: Car },
    { value: 'Furniture', label: 'Furniture', icon: Package },
    { value: 'Equipment', label: 'Equipment', icon: HardDrive },
    { value: 'Other', label: 'Other', icon: Package }
  ];

  const statusOptions = [
    { value: 'Available', label: 'Available', color: '#10b981' },
    { value: 'Assigned', label: 'Assigned', color: '#3b82f6' },
    { value: 'Under Maintenance', label: 'Under Maintenance', color: '#f59e0b' },
    { value: 'Retired', label: 'Retired', color: '#6b7280' },
    { value: 'Lost', label: 'Lost', color: '#ef4444' }
  ];

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setFormData(prev => ({ ...prev, establishment_id: lockedEstId }));
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAssets();
    fetchEstablishments();
    fetchStats();
  }, []);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const response = await assetAPI.getAll();
      setAssets(response.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEstablishments = async () => {
    if (lockedEstId) return; // ClientAdmin: already set
    try {
      const response = await establishmentAPI.getAll();
      setEstablishments(response.establishments || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await assetAPI.getStats();
      setStats(response);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEstablishmentChange = async (establishmentId) => {
    setFormData(prev => ({
      ...prev,
      establishment_id: establishmentId,
      assigned_to_name: '',
      assigned_to_id: ''
    }));
    setEmployees([]);

    if (establishmentId) {
      try {
        const response = await employeeAPI.getByEstablishment(establishmentId);
        setEmployees(response.employees || []);
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    }
  };

  const handleAssignedToChange = (employeeId) => {
    const employee = employees.find(e => e.id === parseInt(employeeId));
    setFormData(prev => ({
      ...prev,
      assigned_to_id: employeeId,
      assigned_to_name: employee ? `${employee.first_name} ${employee.last_name}` : '',
      status: employeeId ? 'Assigned' : 'Available'
    }));
  };

  const resetForm = () => {
    setFormData({
      establishment_id: '',
      asset_tag: '',
      asset_type: '',
      brand_model: '',
      serial_no: '',
      assigned_to_name: '',
      assigned_to_id: '',
      status: 'Available',
      location: '',
      purchase_date: '',
      purchase_cost: '',
      warranty_expiry: '',
      notes: ''
    });
    setEmployees([]);
    setEditingAsset(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.asset_tag?.trim()) {
      alert('Asset Tag is required');
      return;
    }
    if (!formData.asset_type) {
      alert('Asset Type is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        purchase_cost: formData.purchase_cost ? parseFloat(formData.purchase_cost) : null,
        establishment_id: formData.establishment_id ? parseInt(formData.establishment_id) : null,
        assigned_to_id: formData.assigned_to_id ? parseInt(formData.assigned_to_id) : null
      };

      if (editingAsset) {
        await assetAPI.update(editingAsset.id, payload);
        notifySavedAndReload('Asset updated successfully!');
      } else {
        await assetAPI.create(payload);
        notifySavedAndReload('Asset created successfully!');
      }

      resetForm();
      fetchAssets();
      fetchStats();
    } catch (error) {
      console.error('Error saving asset:', error);
      alert(error.message || 'Error saving asset');
    }
  };

  const handleEdit = async (asset) => {
    setFormData({
      establishment_id: asset.establishment_id || '',
      asset_tag: asset.asset_tag || '',
      asset_type: asset.asset_type || '',
      brand_model: asset.brand_model || '',
      serial_no: asset.serial_no || '',
      assigned_to_name: asset.assigned_to_name || '',
      assigned_to_id: asset.assigned_to_id || '',
      status: asset.status || 'Available',
      location: asset.location || '',
      purchase_date: asset.purchase_date || '',
      purchase_cost: asset.purchase_cost || '',
      warranty_expiry: asset.warranty_expiry || '',
      notes: asset.notes || ''
    });

    // Fetch employees for the establishment
    if (asset.establishment_id) {
      try {
        const response = await employeeAPI.getByEstablishment(asset.establishment_id);
        setEmployees(response.employees || []);
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    }

    setEditingAsset(asset);
    setShowForm(true);
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;

    try {
      await assetAPI.delete(assetId);
      notifySavedAndReload('Asset deleted successfully!');
      fetchAssets();
      fetchStats();
    } catch (error) {
      console.error('Error deleting asset:', error);
      alert('Error deleting asset');
    }
  };

  const getStatusColor = (status) => {
    const option = statusOptions.find(s => s.value === status);
    return option?.color || '#6b7280';
  };

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = !searchTerm ||
      asset.asset_tag?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.brand_model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.serial_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.assigned_to_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = !filterStatus || asset.status === filterStatus;
    const matchesType = !filterType || asset.asset_type === filterType;

    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <div className="attendance-container" style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
          Asset Management
        </h1>
        <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
          Track and manage company assets, equipment, and inventory
        </p>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ background: 'var(--theme-surface, #ffffff)', borderRadius: '0.5rem', padding: '1rem', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Total Assets</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#111827' }}>{stats.total_assets || 0}</div>
        </div>
        <div style={{ background: 'var(--theme-surface, #ffffff)', borderRadius: '0.5rem', padding: '1rem', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '0.75rem', color: '#10b981', marginBottom: '0.25rem' }}>Available</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#10b981' }}>{stats.available || 0}</div>
        </div>
        <div style={{ background: 'var(--theme-surface, #ffffff)', borderRadius: '0.5rem', padding: '1rem', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '0.75rem', color: '#3b82f6', marginBottom: '0.25rem' }}>Assigned</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#3b82f6' }}>{stats.assigned || 0}</div>
        </div>
        <div style={{ background: 'var(--theme-surface, #ffffff)', borderRadius: '0.5rem', padding: '1rem', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '0.75rem', color: '#f59e0b', marginBottom: '0.25rem' }}>Maintenance</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#f59e0b' }}>{stats.maintenance || 0}</div>
        </div>
        <div style={{ background: 'var(--theme-surface, #ffffff)', borderRadius: '0.5rem', padding: '1rem', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Total Value</div>
          <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>
            ₹{(stats.total_value || 0).toLocaleString('en-IN')}
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '0.75rem'
      }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            {!searchFocused && !searchTerm && (
              <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
            )}
            <input
              type="text"
              placeholder="Search assets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              style={{
                paddingLeft: searchFocused || searchTerm ? '0.75rem' : '2.25rem',
                paddingRight: '0.75rem',
                paddingTop: '0.5rem',
                paddingBottom: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                width: '200px'
              }}
            />
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              background: 'var(--theme-surface, #ffffff)'
            }}
          >
            <option value="">All Status</option>
            {statusOptions.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              background: 'var(--theme-surface, #ffffff)'
            }}
          >
            <option value="">All Types</option>
            {assetTypes.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          <Plus size={16} />
          Add Asset
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--theme-surface, #ffffff)',
            borderRadius: '0.5rem',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '1.5rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                {editingAsset ? 'Edit Asset' : 'Add New Asset'}
              </h2>
              <button onClick={resetForm} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              {/* Establishment - First field — hidden for ClientAdmin */}
              {!lockedEstId && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="new-employee-field-label">
                  <Building2 size={14} style={{ marginRight: '6px' }} />
                  Organization <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <select
                    value={formData.establishment_id}
                    onChange={(e) => handleEstablishmentChange(e.target.value)}
                    className="new-employee-input"
                    style={{ appearance: 'none', paddingRight: '2rem' }}
                  >
                    <option value="">Select Organization</option>
                    {establishments.map(e => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6b7280' }} />
                </div>
              </div>
              )}

              {/* Asset Tag */}
              <div>
                <label className="new-employee-field-label">
                  <Tag size={14} style={{ marginRight: '6px' }} />
                  Asset Tag <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.asset_tag}
                  onChange={(e) => handleInputChange('asset_tag', e.target.value.toUpperCase())}
                  className="new-employee-input"
                  placeholder="e.g., LAP-001"
                />
              </div>

              {/* Asset Type */}
              <div>
                <label className="new-employee-field-label">
                  <Package size={14} style={{ marginRight: '6px' }} />
                  Asset Type <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <select
                    value={formData.asset_type}
                    onChange={(e) => handleInputChange('asset_type', e.target.value)}
                    className="new-employee-input"
                    style={{ appearance: 'none', paddingRight: '2rem' }}
                  >
                    <option value="">Select Type</option>
                    {assetTypes.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6b7280' }} />
                </div>
              </div>

              {/* Brand/Model */}
              <div>
                <label className="new-employee-field-label">
                  <Monitor size={14} style={{ marginRight: '6px' }} />
                  Brand / Model
                </label>
                <input
                  type="text"
                  value={formData.brand_model}
                  onChange={(e) => handleInputChange('brand_model', e.target.value)}
                  className="new-employee-input"
                  placeholder="e.g., Dell Latitude 5420"
                />
              </div>

              {/* Serial No */}
              <div>
                <label className="new-employee-field-label">
                  <Hash size={14} style={{ marginRight: '6px' }} />
                  Serial Number
                </label>
                <input
                  type="text"
                  value={formData.serial_no}
                  onChange={(e) => handleInputChange('serial_no', e.target.value)}
                  className="new-employee-input"
                  placeholder="e.g., DELL123ABC"
                />
              </div>

              {/* Assigned To */}
              <div>
                <label className="new-employee-field-label">
                  <User size={14} style={{ marginRight: '6px' }} />
                  Assigned To
                </label>
                <div style={{ position: 'relative' }}>
                  <select
                    value={formData.assigned_to_id}
                    onChange={(e) => handleAssignedToChange(e.target.value)}
                    className="new-employee-input"
                    style={{ appearance: 'none', paddingRight: '2rem' }}
                    disabled={!formData.establishment_id}
                  >
                    <option value="">{formData.establishment_id ? 'Select Employee' : 'Select Organization First'}</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} ({emp.employee_id})
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6b7280' }} />
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="new-employee-field-label">
                  Status
                </label>
                <div style={{ position: 'relative' }}>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="new-employee-input"
                    style={{ appearance: 'none', paddingRight: '2rem' }}
                  >
                    {statusOptions.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6b7280' }} />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="new-employee-field-label">
                  <MapPin size={14} style={{ marginRight: '6px' }} />
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="new-employee-input"
                  placeholder="e.g., Chennai Office"
                />
              </div>

              {/* Purchase Date */}
              <div>
                <label className="new-employee-field-label">
                  <Calendar size={14} style={{ marginRight: '6px' }} />
                  Purchase Date
                </label>
                <DateInput
                  value={formData.purchase_date}
                  onChange={(e) => handleInputChange('purchase_date', e.target.value)}
                  className="new-employee-input"
                />
              </div>

              {/* Purchase Cost */}
              <div>
                <label className="new-employee-field-label">
                  <IndianRupee size={14} style={{ marginRight: '6px' }} />
                  Purchase Cost
                </label>
                <input
                  type="number"
                  value={formData.purchase_cost}
                  onChange={(e) => handleInputChange('purchase_cost', e.target.value)}
                  className="new-employee-input"
                  placeholder="e.g., 50000"
                />
              </div>

              {/* Warranty Expiry */}
              <div>
                <label className="new-employee-field-label">
                  <Calendar size={14} style={{ marginRight: '6px' }} />
                  Warranty Expiry
                </label>
                <DateInput
                  value={formData.warranty_expiry}
                  onChange={(e) => handleInputChange('warranty_expiry', e.target.value)}
                  className="new-employee-input"
                />
              </div>

              {/* Notes */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="new-employee-field-label">
                  <FileText size={14} style={{ marginRight: '6px' }} />
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="new-employee-input"
                  rows={3}
                  placeholder="Additional notes about the asset..."
                  style={{ resize: 'vertical' }}
                />
              </div>
            </div>

            {/* Form Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button
                onClick={resetForm}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--theme-surfaceHover)',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                <Save size={16} />
                {editingAsset ? 'Update Asset' : 'Save Asset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assets Table */}
      <div style={{ background: 'var(--theme-surface, #ffffff)', borderRadius: '0.5rem', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: 'var(--theme-surface)', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Asset Tag</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Type</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Brand / Model</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Serial No</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Assigned To</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Status</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Location</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    Loading assets...
                  </td>
                </tr>
              ) : filteredAssets.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    No assets found. Click "Add Asset" to create one.
                  </td>
                </tr>
              ) : (
                filteredAssets.map((asset) => (
                  <tr key={asset.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#111827' }}>
                      {asset.asset_tag}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#374151' }}>
                      {asset.asset_type}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#374151' }}>
                      {asset.brand_model || '-'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {asset.serial_no || '-'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#374151' }}>
                      {asset.assigned_to_name || '-'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        background: `${getStatusColor(asset.status)}20`,
                        color: getStatusColor(asset.status)
                      }}>
                        {asset.status}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#374151' }}>
                      {asset.location || '-'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleEdit(asset)}
                          style={{
                            padding: '0.375rem',
                            background: '#eff6ff',
                            border: 'none',
                            borderRadius: '0.25rem',
                            cursor: 'pointer',
                            color: '#2563eb'
                          }}
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(asset.id)}
                          style={{
                            padding: '0.375rem',
                            background: '#fef2f2',
                            border: 'none',
                            borderRadius: '0.25rem',
                            cursor: 'pointer',
                            color: '#dc2626'
                          }}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Asset;
